import { createRouter, createWebHistory } from 'vue-router'
import LoginComponent from '../views/LoginComponent.vue'
import { authGuard, roleGuard } from './guards'

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: LoginComponent,
    meta: { guest: true }
  },
  // Dashboard
  {
    path: '/',
    name: 'Dashboard',
    component: () => import('../views/DashboardView.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['admin', 'hr']
    },
    beforeEnter: roleGuard(['admin', 'hr'])
  },
  // Admin Profile
  {
    path: '/admin-profile',
    name: 'AdminProfile',
    component: () => import('../views/AdminProfileView.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['admin', 'hr']
    },
    beforeEnter: roleGuard(['admin', 'hr'])
  },
  // Employee Management
  {
    path: '/employee-management',
    name: 'EmployeeManagement',
    component: () => import('../views/EmployeeManagementView.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['admin', 'hr']
    },
    beforeEnter: roleGuard(['admin', 'hr'])
  },
  // Contract Management
  {
    path: '/contract-management',
    name: 'ContractManagement',
    component: () => import('../views/ContractManagementView.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['admin', 'hr']
    },
    beforeEnter: roleGuard(['admin', 'hr'])
  },
  // Performance Reviews
  {
    path: '/performance-reviews',
    name: 'PerformanceReviews',
    component: () => import('../views/PerformanceReviewsView.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['admin', 'hr']
    },
    beforeEnter: roleGuard(['admin', 'hr'])
  },
  // Attendance Management
  {
    path: '/attendance-management',
    name: 'AttendanceManagement',
    component: () => import('../views/AttendanceManagementView.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['admin', 'hr']
    },
    beforeEnter: roleGuard(['admin', 'hr'])
  },
  // Leaves Requests
  {
    path: '/leaves-requests',
    name: 'LeavesRequests',
    component: () => import('../views/LeavesRequestsView.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['admin', 'hr']
    },
    beforeEnter: roleGuard(['admin', 'hr'])
  },
  // Complaints Requests
  {
    path: '/complaints-requests',
    name: 'ComplaintsRequests',
    component: () => import('../views/ComplaintsRequestsView.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['admin', 'hr']
    },
    beforeEnter: roleGuard(['admin', 'hr'])
  },
  // Rewards and Penalties
  {
    path: '/rewards-penalties',
    name: 'RewardsAndPenalties',
    component: () => import('../views/RewardsAndPenaltiesView.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['admin', 'hr']
    },
    beforeEnter: roleGuard(['admin', 'hr'])
  },
  // Departments Management
  {
    path: '/departments-management',
    name: 'DepartmentsManagement',
    component: () => import('../views/DepartmentsManagementView.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['admin', 'hr']
    },
    beforeEnter: roleGuard(['admin', 'hr'])
  },
  // Companies Management
  {
    path: '/companies-management',
    name: 'CompaniesManagement',
    component: () => import('../views/CompaniesManagementView.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['admin', 'hr']
    },
    beforeEnter: roleGuard(['admin', 'hr'])
  },
  
  // Settings with Nested Routes
  {
    path: '/settings',
    component: () => import('../views/SettingsView.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['admin', 'hr']
    },
    beforeEnter: roleGuard(['admin', 'hr']),
    children: [
      {
        path: '',
        name: 'Settings',
        component: { template: '<div></div>' }
      },
      {
        path: 'roles-permissions',
        name: 'RolesPermissionsSettings',
        component: () => import('../views/settings/RolesPermissionsSettings.vue'),
        meta: { 
          requiresAuth: true,
          roles: ['admin']
        },
        beforeEnter: roleGuard(['admin'])
      },
      {
        path: 'leave-types',
        name: 'LeaveTypesSettings',
        component: () => import('../views/settings/LeaveTypesSettings.vue')
      },
      {
        path: 'contract-types',
        name: 'ContractTypesSettings',
        component: () => import('../views/settings/ContractTypesSettings.vue')
      },
      {
        path: 'complaint-types',
        name: 'ComplaintTypesSettings',
        component: () => import('../views/settings/ComplaintTypesSettings.vue')
      },
      {
        path: 'reward-types',
        name: 'RewardTypesSettings',
        component: () => import('../views/settings/RewardTypesSettings.vue')
      },
      {
        path: 'penalty-types',
        name: 'PenaltyTypesSettings',
        component: () => import('../views/settings/PenaltyTypesSettings.vue')
      },
      {
        path: 'employee-positions',
        name: 'EmployeePositionsSettings',
        component: () => import('../views/settings/EmployeePositionsSettings.vue')
      },
      {
        path: 'document-types',
        name: 'DocumentTypesSettings',
        component: () => import('../views/settings/DocumentTypesSettings.vue')
      },
      {
        path: 'employment-types',
        name: 'EmploymentTypesSettings',
        component: () => import('../views/settings/EmploymentTypesSettings.vue')
      },
      {
        path: 'countries',
        name: 'CountriesSettings',
        component: () => import('../views/settings/CountriesSettings.vue')
      },
      {
        path: 'cities',
        name: 'CitiesSettings',
        component: () => import('../views/settings/CitiesSettings.vue')
      }
    ]
  },
  
  // Employee Routes
  {
    path: '/profile',
    name: 'Profile',
    component: () => import('../views/employee/ProfileView.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['employee']
    },
    beforeEnter: roleGuard(['employee'])
  },
  {
    path: '/my-attendance',
    name: 'MyAttendance',
    component: () => import('../views/employee/MyAttendanceView.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['employee']
    },
    beforeEnter: roleGuard(['employee'])
  },
  {
    path: '/my-leaves',
    name: 'MyLeaves',
    component: () => import('../views/employee/MyLeavesView.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['employee']
    },
    beforeEnter: roleGuard(['employee'])
  },
  {
    path: '/request-leave',
    name: 'RequestLeave',
    component: () => import('../views/employee/RequestLeaveView.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['employee']
    },
    beforeEnter: roleGuard(['employee'])
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

router.beforeEach(authGuard)

export default router 